import { t } from "../../lib/i18n";
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ArticleCard from '../../components/blog/ArticleCard'; // Assuming relative path
import PublicNavbar from '../../components/PublicNavbar'; // Import the new PublicNavbar
import Navbar from '../../components/Navbar';
import { auth } from '../../firebaseClient'; // Import auth client

import initialArticlesData from '../../data/articles_metadata.json';

interface ArticleMetadata {
  title: string;
  slug: string;
  publish_date?: string;
  published_at?: string;
  featured_image?: string;
}

const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL || 'https://cre-dominion-backend-1006317416349.us-central1.run.app';

const getInitialArticles = (): ArticleMetadata[] => {
  try {
    const cached = sessionStorage.getItem('cre_blog_articles_cache') || localStorage.getItem('cre_blog_articles_cache');
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (e) {}
  return (initialArticlesData as ArticleMetadata[]) || [];
};

const BlogListPage: React.FC = () => {
  const [articles, setArticles] = useState<ArticleMetadata[]>(getInitialArticles);
  const [loading, setLoading] = useState<boolean>(() => getInitialArticles().length === 0);
  const [error, setError] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(!!auth.currentUser);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const articlesPerPage = 24;

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setIsLoggedIn(!!user);
    });

    const fetchArticles = async () => {
      try {
        const response = await axios.get<ArticleMetadata[]>(`${API_BASE_URL}/blog/articles`);
        
        // Sort newest first robustly using both possible schema date fields
        const sortedArticles = response.data.sort((a, b) => {
          const dateAStr = a.published_at || a.publish_date || '';
          const dateBStr = b.published_at || b.publish_date || '';
          let dateA = dateAStr ? new Date(dateAStr).getTime() : 0;
          let dateB = dateBStr ? new Date(dateBStr).getTime() : 0;
          if (isNaN(dateA)) dateA = 0;
          if (isNaN(dateB)) dateB = 0;
          return dateB - dateA;
        });

        setArticles(sortedArticles);
        setLoading(false);
        try {
          sessionStorage.setItem('cre_blog_articles_cache', JSON.stringify(sortedArticles));
          localStorage.setItem('cre_blog_articles_cache', JSON.stringify(sortedArticles));
        } catch (e) {}
      } catch (err) {
        console.error('Error fetching articles:', err);
        if (articles.length === 0) {
          setError('Failed to load blog posts.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
    return () => unsubscribe();
  }, []);

  // Pagination logic
  const indexOfLastArticle = currentPage * articlesPerPage;
  const indexOfFirstArticle = indexOfLastArticle - articlesPerPage;
  const currentArticles = articles.slice(indexOfFirstArticle, indexOfLastArticle);
  const totalPages = Math.ceil(articles.length / articlesPerPage);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = () => {
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + 4);

    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  if (loading && articles.length === 0) return <div className="text-center p-8">{t("blogListPage.k1")}</div>;
  if (error) return <div className="text-center p-8 text-red-500">{t("blogListPage.k2")} {error}</div>;

  return (
    <>
      <Helmet>
        <title>{t("blogListPage.k3")}</title>
        <meta name="description" content="Explore our comprehensive library of expert articles on commercial real estate investing, leasing, market trends, and property management." />
        <link rel="canonical" href="https://credominion.com/blog" />
      </Helmet>
      {isLoggedIn ? <Navbar handleSignOut={async () => { await auth.signOut(); setIsLoggedIn(false); }} /> : <PublicNavbar />}
      <div className="container mx-auto p-4">
        <h1 className="text-4xl font-bold text-center mb-8">{t("blogListPage.k4")}</h1>
        
        {articles.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {currentArticles.map((article) => (
                <Link to={`/blog/${article.slug}`} key={article.slug}>
                  <ArticleCard article={article} />
                </Link>
              ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center mt-12 gap-2">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label={t("blogListPage.k5")}
                  className={`px-4 py-2 rounded-lg border ${
                    currentPage === 1 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                      : 'bg-white text-blue-600 hover:bg-blue-50 border-blue-200'
                  }`}
                >
                  {t("blogListPage.k6")} </button>
                
                <div className="flex gap-1">
                  {getPageNumbers().map((number) => (
                    <button
                      key={number}
                      onClick={() => paginate(number)}
                      aria-label={`Page ${number}`}
                      className={`w-10 h-10 rounded-lg border transition-colors duration-200 ${
                        currentPage === number
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-blue-600 hover:bg-blue-50 border-blue-200'
                      }`}
                    >
                      {number}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  aria-label={t("blogListPage.k7")}
                  className={`px-4 py-2 rounded-lg border ${
                    currentPage === totalPages 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                      : 'bg-white text-blue-600 hover:bg-blue-50 border-blue-200'
                  }`}
                >
                  {t("blogListPage.k8")} </button>
              </div>
            )}
          </>
        ) : (
          <p className="text-center text-gray-600 mt-12">{t("blogListPage.k9")}</p>
        )}
      </div>
    </>
  );
};

export default BlogListPage;
